import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useFeatureFlags } from './useFeatureFlags.jsx';
import unifiedApi from '../services/unifiedApi';
import eventBus, { ASSIGNMENT_EVENTS } from '../utils/eventBus';
import { toast } from 'sonner';
import logger from '@/utils/logger';

export const BUS_STATUS_COLOR = {
  active: 'bg-green-100 text-green-800',
  maintenance: 'bg-yellow-100 text-yellow-800',
  inactive: 'bg-red-100 text-red-800',
  out_of_service: 'bg-red-100 text-red-800',
};

export const BUS_STATUS_LABEL = {
  active: 'Actif',
  maintenance: 'Maintenance',
  inactive: 'Hors Service',
  out_of_service: 'Hors Service',
};

export const getStatusColor = (status) => BUS_STATUS_COLOR[status] || 'bg-gray-100 text-gray-800';
export const getStatusLabel = (status) => BUS_STATUS_LABEL[status] || 'Inconnu';

/**
 * `buses.model` stocke « Marque Modèle » (GET /admin/buses le redécoupe en brand + model).
 * Pas de doublon si le modèle commence déjà par la marque (données saisies avant que la modale
 * n'émette la marque : « Sprinter » relu comme brand=Sprinter, model='').
 */
export const buildBusModel = (brand, model) => {
  const b = (brand || '').trim();
  const m = (model || '').trim();
  if (!b) return m;
  if (!m) return b;
  return m.toLowerCase().startsWith(b.toLowerCase()) ? m : `${b} ${m}`;
};

export const useBuses = () => {
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [filters, setFilters] = useState({
    search: searchParams.get('q') || '',
    status: searchParams.get('status') || 'active',
  });
  const [showModal, setShowModal] = useState(false);
  const [editingBus, setEditingBus] = useState(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [busToDelete, setBusToDelete] = useState(null);

  const { hasFeature, isTabEnabled } = useFeatureFlags();

  const {
    data: busesData,
    isPending: loading,
    error: queryError,
    refetch: loadBuses,
  } = useQuery({
    queryKey: ['buses'],
    queryFn: async () => {
      const [busesResponse, statsResponse] = await Promise.all([
        unifiedApi.buses.getAll(),
        hasFeature('metrics') ? unifiedApi.buses.getStats() : Promise.resolve(null),
      ]);
      const list = busesResponse.data || [];
      const stats = statsResponse?.data
        ? {
            total: statsResponse.data.total_buses || 0,
            active: statsResponse.data.active_buses || 0,
            maintenance: statsResponse.data.maintenance_buses || 0,
            inactive: statsResponse.data.inactive_buses || 0,
          }
        : {
            total: list.length,
            active: list.filter((b) => b.status === 'active').length,
            maintenance: list.filter((b) => b.status === 'maintenance').length,
            inactive: list.filter((b) => b.status === 'inactive' || b.status === 'out_of_service').length,
          };
      return { list, stats };
    },
  });

  const buses = busesData?.list ?? [];
  const stats = busesData?.stats ?? {};
  const error = queryError?.message ?? null;

  useEffect(() => {
    const invalidate = () => queryClient.invalidateQueries({ queryKey: ['buses'] });
    const cleanupFns = [
      eventBus.on(ASSIGNMENT_EVENTS.ROUTE_UPDATED, invalidate),
      eventBus.on(ASSIGNMENT_EVENTS.DRIVER_UPDATED, invalidate),
      eventBus.on(ASSIGNMENT_EVENTS.ASSIGNMENT_CHANGED, (eventData) => {
        if (eventData.data.type === 'route' || eventData.data.type === 'driver') invalidate();
      }),
    ];
    return () => cleanupFns.forEach((fn) => fn());
  }, [queryClient]);

  const filteredBuses = buses.filter((bus) => {
    const matchesSearch =
      !filters.search ||
      bus.bus_number?.toLowerCase().includes(filters.search.toLowerCase()) ||
      bus.license_plate?.toLowerCase().includes(filters.search.toLowerCase()) ||
      bus.model?.toLowerCase().includes(filters.search.toLowerCase());
    const matchesStatus = !filters.status || bus.status === filters.status;
    return matchesSearch && matchesStatus;
  });

  const updateFilters = (newFilters) => {
    const merged = { ...filters, ...newFilters };
    setFilters(merged);
    const params = new URLSearchParams(searchParams);
    if (merged.search) params.set('q', merged.search);
    else params.delete('q');
    if (merged.status) params.set('status', merged.status);
    else params.delete('status');
    setSearchParams(params, { replace: true });
  };

  const handleAdd = () => {
    setEditingBus(null);
    setShowModal(true);
  };

  const handleEdit = (bus) => {
    setEditingBus(bus);
    setShowModal(true);
  };

  const handleDeleteBus = (bus) => {
    setBusToDelete(bus);
    setDeleteModalOpen(true);
  };

  const confirmDeleteBus = async () => {
    const bus = busToDelete;
    setDeleteModalOpen(false);
    setBusToDelete(null);
    if (!bus) return;

    queryClient.setQueryData(['buses'], (old) => {
      if (!old) return old;
      return { ...old, list: old.list.filter((b) => b.id !== bus.id) };
    });

    try {
      const result = await unifiedApi.buses.delete(bus.id);
      if (!result.success) throw new Error(result.error || 'Erreur suppression');
      toast.success(`Bus ${bus.bus_number} supprimé`);
      queryClient.invalidateQueries({ queryKey: ['buses'] });
    } catch (err) {
      logger.error('Erreur suppression bus:', err);
      queryClient.invalidateQueries({ queryKey: ['buses'] });
      toast.error(`Erreur lors de la suppression du bus ${bus.bus_number}`);
    }
  };

  const handleSave = async (busData) => {
    const backendData = {
      bus_number: busData.busId || busData.bus_number || `BUS-${Date.now()}`,
      license_plate: busData.licensePlate || busData.license_plate,
      model: buildBusModel(busData.brand, busData.model),
      capacity: parseInt(busData.capacity) || 25,
      year: parseInt(busData.year) || new Date().getFullYear(),
      color: busData.color || 'Blanc',
      fuel_type: busData.fuel_type || busData.fuelType || 'diesel',
      status: busData.status || 'active',
      notes: busData.notes || '',
      imei: busData.imei || null,
      ignition_reliable: busData.ignition_reliable !== false,
      engine_power_cv: busData.engine_power_cv ?? null,
      transmission: busData.transmission || null,
      odometer_km: busData.odometer_km === undefined || busData.odometer_km === '' ? null : Number(busData.odometer_km),
      last_maintenance_date: busData.last_maintenance_date || null,
    };

    const featureFlags = {
      technique: true,
      maintenance: isTabEnabled('buses', 'maintenance'),
    };

    try {
      let result;
      if (editingBus) {
        result = await unifiedApi.buses.update(editingBus.id, backendData, featureFlags);
        if (result.success) {
          toast.success('Bus modifié avec succès !');
          setShowModal(false);
          queryClient.invalidateQueries({ queryKey: ['buses'] });
        } else {
          throw new Error(result.error || 'Erreur lors de la modification');
        }
      } else {
        result = await unifiedApi.buses.create(backendData, featureFlags);
        if (result.success) {
          toast.success('Bus créé avec succès !');
          setShowModal(false);
          queryClient.invalidateQueries({ queryKey: ['buses'] });
        } else {
          throw new Error(result.error || 'Erreur lors de la création');
        }
      }
    } catch (err) {
      logger.error('Erreur sauvegarde bus:', err);
      let errorMessage = 'Erreur lors de la sauvegarde du bus';
      if (err.response?.data) {
        errorMessage =
          typeof err.response.data === 'object' && err.response.data.message
            ? err.response.data.message
            : err.response.data;
      } else if (err.message) {
        errorMessage = err.message;
      }
      toast.error(errorMessage);
    }
  };

  return {
    buses,
    filteredBuses,
    loading,
    error,
    stats,
    filters,
    showModal,
    editingBus,
    deleteModalOpen,
    busToDelete,
    updateFilters,
    setShowModal,
    setDeleteModalOpen,
    loadBuses,
    handleAdd,
    handleEdit,
    handleDeleteBus,
    confirmDeleteBus,
    handleSave,
  };
};

export default useBuses;
